/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperSearch } from '@/scapi';
import { Checkbox } from '@/components/forms/checkbox';
import type { RefinementProps } from '../types';

/**
 * Default refinement renderer — checkbox list (Figma node 13055:342244, "Item Variants").
 *
 * Row layout follows Figma exactly: `[checkbox] Label (count)`. Label and count live in
 * the SAME text node, with the count immediately following the label in parentheses —
 * not right-aligned via a flex spacer. The count is muted to keep visual emphasis on
 * the option name.
 */
export default function DefaultRefinement({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    return (
        <ul className="mt-2 flex flex-col gap-1">
            {values.map((value: ShopperSearch.schemas['ProductSearchRefinementValue'], idx) => {
                const id = `refine-${attributeId}-${idx}`;
                const isSelected = isFilterSelected(attributeId, value.value);

                return (
                    <li key={`${attributeId}:${value.value}`}>
                        <label htmlFor={id} className="flex w-full cursor-pointer items-center gap-2 py-1">
                            <Checkbox
                                id={id}
                                checked={isSelected}
                                onCheckedChange={() => toggleFilter(attributeId, value.value)}
                                className="size-4"
                            />
                            <span className="text-sm leading-5 text-foreground">
                                {value.label || value.value}
                                {value.hitCount !== undefined && (
                                    <span className="ml-1 tabular-nums text-muted-foreground">({value.hitCount})</span>
                                )}
                            </span>
                        </label>
                    </li>
                );
            })}
        </ul>
    );
}
